import React, { useCallback, useMemo, memo } from "react";
import WorkforceSuggestionComponent from "../../../generic/workforcesuggestion/WorkforceSuggestionComponent";
import { useGetOrgIdentifier } from "../../../generic/hooks/useGetOrgIdentifier";

const FILTER_VALUES = { SERVICES: "Services", DISTANCE: "Distance", PREFERENCES: "Preferences" };
const ALL_FILTER_VALUES = Object.values(FILTER_VALUES);

/**
 * Workforce suggestion payload is memoized so that the suggestion API is called
 * ONLY when service or patient (or filters) change — not on volunteer selection,
 * slot edits, or opportunity updates. This prevents unnecessary re-fetches.
 */
const WorkforceSuggestionWrapper = ({
  setBroadcastData,
  typesOfServicesSelected,
  patientName,
  opportunities,
  showSelectAll,
  isMultiSelect,
  filterRequired,
  showDetails,
  selectedFilters = [],
  isTeamRequired
}) => {
  const organizationIdentifier = useGetOrgIdentifier();

  // Stable callback to avoid re-renders of WorkforceSuggestionComponent
  const onWorkforceSuggestionDataChangeCallback = useCallback(
    (data) => {
      setBroadcastData(data);
    },
    [setBroadcastData]
  );

  // Payload changes only when service, patient, or filters change — NOT when opportunities change.
  // So the suggestion API runs only when needed (service/patient change), not on assign/remove.
  const generateWorkforceEntityRecommenderPayloadData = useMemo(() => {
    const patient = JSON.parse(patientName?.value || "{}");
    const activeFilters =
      Array.isArray(selectedFilters) && selectedFilters.length > 0
        ? selectedFilters.map((f) => (typeof f === "string" ? f : f?.value))
        : ALL_FILTER_VALUES;
    const hasDistance = activeFilters.includes(FILTER_VALUES.DISTANCE);
    const hasServices = activeFilters.includes(FILTER_VALUES.SERVICES);
    const hasPreferences = activeFilters.includes(FILTER_VALUES.PREFERENCES);

    return {
      organizationIdentifier,
      excludeIdentifiers: (opportunities || [])
        .filter((item) => item.opportunityStatus !== "Unassigned")
        .map((opportunity) => opportunity.workforceIdentifier),
      teamIds: isTeamRequired ? patient?.teams?.map((team) => team?.id) : [],
      ...(hasServices && {
        services: Array.isArray(typesOfServicesSelected)
          ? typesOfServicesSelected.map((item) => item?.label)
          : [typesOfServicesSelected?.label],
      }),
      ...(hasPreferences && {
        patientIdentifier: patient?.userIdentifier,
      }),
      ...(hasDistance && {
        latitude: patient?.latitude,
        longitude: patient?.longitude,
      }),
      isStrictMatch: Boolean(selectedFilters?.length),
      showJustification: true
    };
  // Intentionally exclude 'opportunities' so suggestion API is not triggered on assign/remove.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API must only run when service/patient/filters change
  }, [
    organizationIdentifier,
    patientName?.value,
    typesOfServicesSelected,
    selectedFilters,
  ]);

  return (
    <WorkforceSuggestionComponent
      handleSelectedUserData={onWorkforceSuggestionDataChangeCallback}
      workforceEntityRecommenderPayloadData={generateWorkforceEntityRecommenderPayloadData}
      showDistanceSlider={true}
      showSelectAll={showSelectAll}
      isMultiSelect={isMultiSelect}
      filterRequired={filterRequired}
      showDetails={showDetails}
    />
  );
};

// Re-render only when props that affect the suggestion payload or behavior change.
// Ignore opportunities change so we don't re-render (and trigger child/API) on assign/remove.
function arePropsEqual(prev, next) {
  return (
    prev.setBroadcastData === next.setBroadcastData &&
    prev.typesOfServicesSelected === next.typesOfServicesSelected &&
    prev.patientName?.value === next.patientName?.value &&
    prev.showSelectAll === next.showSelectAll &&
    prev.isMultiSelect === next.isMultiSelect &&
    prev.filterRequired === next.filterRequired &&
    prev.selectedFilters === next.selectedFilters
  );
}

export default memo(WorkforceSuggestionWrapper, arePropsEqual);
